// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — WasteDrillDown
// Detailed view when user clicks "Waste & Leakage" KPI
// ══════════════════════════════════════════════════════════════════════════════

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  TrendingUp, DollarSign, AlertTriangle, Percent,
  ArrowLeft, ArrowRight
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card, Button, Grid, Badge } from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';

export function WasteDrillDown({ data, onBack }) {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();

  const employees = data?.employees || [];
  const gaps = data?.gaps || [];

  // Group gaps by type for chart
  const gapsByCategory = gaps.reduce((acc, g) => {
    const key = g.gap_type;
    if (!acc[key]) {
      acc[key] = {
        category_ar: g.gap_type === 'inventory_mismatch' ? 'فجوات مخزون' :
                     g.gap_type === 'revenue_leak' ? 'تسرب إيرادات' : 'تكاليف غير مبررة',
        category_en: g.gap_type === 'inventory_mismatch' ? 'Inventory Gaps' :
                     g.gap_type === 'revenue_leak' ? 'Revenue Leaks' : 'Unexplained Costs',
        expected: 0,
        actual: 0
      };
    }
    acc[key].expected += parseFloat(g.expected_value || 0);
    acc[key].actual += parseFloat(g.actual_value || 0);
    return acc;
  }, {});

  const chartData = Object.values(gapsByCategory);
  const totalExpected = chartData.reduce((s, d) => s + d.expected, 0);
  const totalActual = chartData.reduce((s, d) => s + d.actual, 0);
  const totalGap = totalExpected - totalActual;
  const leakRate = totalExpected > 0 ? ((totalGap / totalExpected) * 100).toFixed(1) : 0;

  // Top cancellers
  const topCancellers = [...employees]
    .filter(e => e.total_cancellations > 40)
    .sort((a, b) => b.total_cancellations - a.total_cancellations)
    .slice(0, 5);

  const tooltipStyle = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px'
  };

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 20 }}>
        <BackIcon size={14} /> {t.back}
      </Button>

      <div style={{ marginBottom: 24 }}>
        <h2 style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 800,
          color: theme.text,
          letterSpacing: -0.3
        }}>
          {t.wasteTitle}
        </h2>
        <p style={{
          margin: '4px 0 0',
          fontSize: 13,
          color: theme.textSecondary
        }}>
          {t.wasteDesc}
        </p>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Grid min={200}>
          <StatCard
            icon={<TrendingUp size={20} color={theme.info} />}
            label={t.expectedRevenue}
            value={`${t.sar} ${(totalExpected / 1000).toFixed(1)}K`}
            accentColor={theme.info}
          />
          <StatCard
            icon={<DollarSign size={20} color={theme.success} />}
            label={t.actualRevenue}
            value={`${t.sar} ${(totalActual / 1000).toFixed(1)}K`}
            accentColor={theme.success}
          />
          <StatCard
            icon={<AlertTriangle size={20} color={theme.danger} />}
            label={t.wasteLeakage}
            value={`${t.sar} ${(totalGap / 1000).toFixed(1)}K`}
            accentColor={theme.danger}
          />
          <StatCard
            icon={<Percent size={20} color={theme.danger} />}
            label={t.leakageRate}
            value={`${leakRate}%`}
            accentColor={theme.danger}
          />
        </Grid>
      </div>

      <Grid min={380}>
        <Card style={{ padding: 22 }}>
          <h4 style={{
            margin: '0 0 16px',
            fontSize: 14,
            fontWeight: 700,
            color: theme.text
          }}>
            {lang === 'ar' ? 'التسرب حسب الفئة' : 'Leakage by Category'}
          </h4>
          {chartData.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              color: theme.textTertiary,
              fontSize: 13
            }}>
              {lang === 'ar' ? 'لا توجد فجوات مسجلة' : 'No gaps recorded'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                <XAxis
                  dataKey={lang === 'ar' ? 'category_ar' : 'category_en'}
                  tick={{ fill: theme.textTertiary, fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: theme.textTertiary, fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="expected"
                  fill={theme.chartColors[1]}
                  radius={[4, 4, 0, 0]}
                  name={t.expectedValue}
                />
                <Bar
                  dataKey="actual"
                  fill={theme.chartColors[0]}
                  radius={[4, 4, 0, 0]}
                  name={t.actualValue}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card style={{ padding: 22 }}>
          <h4 style={{
            margin: '0 0 16px',
            fontSize: 14,
            fontWeight: 700,
            color: theme.text
          }}>
            {lang === 'ar' ? 'أعلى معدلات إلغاء' : 'Top Cancellers'}
          </h4>
          {topCancellers.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              color: theme.textTertiary,
              fontSize: 13
            }}>
              {lang === 'ar' ? 'لا توجد حالات' : 'No flagged employees'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    {[t.fullName, lang === 'ar' ? 'إلغاءات' : 'Cancels',
                      lang === 'ar' ? 'المعدل' : 'Rate', t.status].map(h => (
                      <th key={h} style={{
                        padding: '10px 12px',
                        textAlign: isRTL ? 'right' : 'left',
                        fontSize: 11,
                        fontWeight: 600,
                        color: theme.textTertiary
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topCancellers.map(e => {
                    const rate = e.total_transactions > 0
                      ? ((e.total_cancellations / e.total_transactions) * 100).toFixed(1)
                      : 0;
                    return (
                      <tr key={e.id} style={{ borderBottom: `1px solid ${theme.borderLight}` }}>
                        <td style={{
                          padding: '10px 12px',
                          fontSize: 13,
                          fontWeight: 600,
                          color: theme.text
                        }}>
                          {lang === 'ar' ? e.name_ar : e.name_en}
                        </td>
                        <td style={{
                          padding: '10px 12px',
                          fontSize: 13,
                          fontWeight: 700,
                          color: theme.danger
                        }}>
                          {e.total_cancellations}
                        </td>
                        <td style={{
                          padding: '10px 12px',
                          fontSize: 13,
                          color: theme.danger
                        }}>
                          {rate}%
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Badge variant="danger">{t.flagged}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </Grid>
    </div>
  );
}
